import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ApiService} from '../../@core/utils/api.service';
import {isSystemAvatarUserId} from './user-avatar.util';

@Injectable({providedIn: 'root'})
export class UserAvatarService {
	private readonly cache = new Map<number, boolean>();
	private readonly version = new Map<number, number>();
	private readonly updated$ = new Subject<number>();

	constructor(private apiService: ApiService) {}

	watchUpdates(userId: number | string | null | undefined): Observable<number> {
		const numericId = Number(userId);
		return this.updated$.pipe(filter((id) => id === numericId));
	}

	hasCustomPic(userId: number | string | null | undefined): Observable<boolean> {
		if (userId == null || isSystemAvatarUserId(userId)) {
			return of(true);
		}

		const numericId = Number(userId);
		if (Number.isNaN(numericId)) {
			return of(false);
		}

		if (this.cache.has(numericId)) {
			return of(this.cache.get(numericId) as boolean);
		}

		return this.apiService.hasUserProfilePic(numericId).pipe(
			map((response) => !!response?.has_pic),
			tap((hasPic) => this.cache.set(numericId, hasPic)),
			catchError(() => {
				this.cache.set(numericId, false);
				return of(false);
			}),
		);
	}

	getPicUrl(userId: number | string): string {
		const version = this.version.get(Number(userId));
		const suffix = version ? `&t=${version}` : '';
		return `${environment.apiUrl}/user_pic?id=${userId}${suffix}`;
	}

	invalidate(userId: number | string | null | undefined): void {
		if (userId == null || isSystemAvatarUserId(userId)) {
			return;
		}
		const numericId = Number(userId);
		if (Number.isNaN(numericId)) {
			return;
		}
		this.cache.delete(numericId);
		this.version.set(numericId, Date.now());
		this.updated$.next(numericId);
	}
}
